import axios from 'axios';
import { getAuthToken, isAuthTokenExpired } from './utils';

// The app signs API calls with the Auth0 ID token from Auth0's cache, and nothing renewed it once it expired:
// every call then failed with 401 and the pages just went empty. This keeps calls signed in. An expired token
// is renewed before a request goes out, and a request the API still rejects with 401 is retried once with a
// renewed token. When the token cannot be renewed (for example there is no refresh token), the user is sent to
// sign in again.

const lastSignInKey = 'authSession.lastSignInRedirect';
const signInRetryWindowMs = 2 * 60 * 1000;

let auth0 = null;     // { getAccessTokenSilently, loginWithRedirect } from useAuth0()
let renewing = null;  // the renewal in flight, shared by requests that fail together
let signingIn = false;

const signInAgain = () => {
  if (signingIn || !auth0) return;
  // A second redirect this soon means signing in did not help, so stop rather than loop.
  const lastRedirect = Number(sessionStorage.getItem(lastSignInKey) || 0);
  if (Date.now() - lastRedirect < signInRetryWindowMs) {
    console.warn('Sign-in was renewed moments ago and the token is still rejected; not redirecting again.');
    return;
  }
  sessionStorage.setItem(lastSignInKey, String(Date.now()));
  signingIn = true;
  auth0.loginWithRedirect();
};

// Resolves once Auth0's cache holds a renewed ID token. Bypassing the cache makes Auth0 fetch new tokens,
// and it stores the new ID token where getAuthToken() reads it.
const renewToken = () => {
  if (!renewing) {
    renewing = auth0.getAccessTokenSilently({ cacheMode: 'off' }).finally(() => {
      renewing = null;
    });
  }
  return renewing;
};

const renewOrSignIn = async () => {
  try {
    await renewToken();
  } catch (error) {
    signInAgain();
    throw error;
  }
};

// The current ID token, renewed first if it has expired. Used for the SignalR connection.
export const getFreshAuthToken = async () => {
  if (auth0 && isAuthTokenExpired()) {
    await renewOrSignIn();
  }
  return getAuthToken();
};

const hasBearerToken = (config) => {
  const value = config?.headers?.get ? config.headers.get('authorization') : config?.headers?.authorization;
  return typeof value === 'string' && value.startsWith('Bearer ');
};

const setBearerToken = (config, token) => {
  config.headers.set('authorization', `Bearer ${token}`);
};

// Installs the axios interceptors for the signed-in session and returns a function that removes them.
export const startAuthSession = ({ getAccessTokenSilently, loginWithRedirect }) => {
  auth0 = { getAccessTokenSilently, loginWithRedirect };

  const requestInterceptor = axios.interceptors.request.use(async (config) => {
    if (hasBearerToken(config) && isAuthTokenExpired()) {
      await renewOrSignIn();
      setBearerToken(config, getAuthToken());
    }
    return config;
  });

  const responseInterceptor = axios.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    // No cached token means the user has not signed in yet, which the app's own sign-in flow handles.
    if (error?.response?.status !== 401 || !hasBearerToken(config) || !getAuthToken() || config._authRetried) {
      throw error;
    }
    config._authRetried = true;
    try {
      await renewToken();
    } catch {
      // Sign in again only for an expired token. A token rejected for another reason would be rejected
      // again after signing in, and redirecting would loop.
      if (isAuthTokenExpired()) signInAgain();
      throw error;
    }
    setBearerToken(config, getAuthToken());
    return axios(config);
  });

  return () => {
    axios.interceptors.request.eject(requestInterceptor);
    axios.interceptors.response.eject(responseInterceptor);
    auth0 = null;
  };
};
